import { memo, useMemo } from "react";
import { css } from "@stubhub/broadway-foundation/css";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { Col, IconsSize, MusicIcon } from "@viagogo/core/ui";
import { AddFeature, RecordParticipationTypes, RemoveFeature, useFeatureTernary } from "@viagogo/feature";
import { useFormattedContent } from "src/shared/components/content";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { useAppContext } from "src/shared/context/AppContext";
import { usePageModelData } from "src/shared/context/PageModelDataContext";
import { useIsStubhub } from "src/shared/utils/appContextHelper";
import {
  MusicNoteIconWrapper,
  PerformerGenresDiv,
} from "src/website-apps/shared/components/hero-banner/ImmersiveHeroBanner/ImmersiveHeroBanner.styled";
import { SmallerPerformerFontFeature } from "src/website-apps/shared/components/hero-banner/ImmersiveHeroBanner/SmallerPerformerFontFeature";
import { GRID_FILTER_TYPES } from "src/website-apps/shared/components/immersive-performer/eventlist-filters/constants";
import { TOP_LEVEL_CATEGORY } from "src/website-apps/shared/constants/TopLevelCategory";
import { useEventsGridContext } from "src/website-apps/shared/context/events-grid-context";
import { usePerformerInfoCardData } from "src/website-apps/shared/hooks/usePerformerInfoCardData";
import { isPerformerPage } from "src/website-apps/shared/utils/isPerformerPage";

export const TIM_MCGRAW_EXCLUSIVE_CATEGORY_ID = 150_451_037;
const COL_SPAN_PROP = [11];

const desktopTourSubtitleStyle = css({
  textStyle: "body.medium",
  color: "text",
  marginBlockStart: "primitives.100",
});

export const ArtistInfoColumn = memo<{ categoryName: string }>(({ categoryName }) => {
  const isDesktop = useMediaQuery(Breakpoint.Desktop);
  const isMobile = !useMediaQuery(Breakpoint.Tablet);
  const appContext = useAppContext();
  const isStubHub = useIsStubhub();
  const model = usePageModelData();
  const { genres } = usePerformerInfoCardData();
  const [state] = useEventsGridContext();

  const isParking = useMemo(() => state.filters.gridFilterType === GRID_FILTER_TYPES.PARKING, [state]);

  /** \{categoryName\} parking */
  const categoryNameWithParking = useFormattedContent("cid$#3789327#", [categoryName]);
  const hasCategorySummary = "categorySummary" in model && !!model.categorySummary;
  const hasSeoCity = "seoCity" in model && !!model.seoCity;

  const parkingContentId =
    hasCategorySummary && model.categorySummary?.topLevelCategoryId === 2 ? "cid$#3835736#" : "cid$#3833898#";
  /** \{categoryName\} in \{cityName\} parking */
  const categoryNameWithLocationAndParking = useFormattedContent(parkingContentId, [
    categoryName,
    hasSeoCity ? (model.seoCity?.cityName ?? "") : "",
  ]);

  const categoryTitle = hasCategorySummary ? model.categorySummary?.categoryTitle : "";

  const parkingTitleContentTernary = useFeatureTernary(FeatureSetEnum.TMEventListSEOCityFilterKillswitch, () =>
    hasSeoCity ? !!model.seoCity?.cityName : false,
  );
  const parkingTitleContent = parkingTitleContentTernary({
    control: () => categoryNameWithParking,
    variant: () => categoryNameWithLocationAndParking,
  });

  const isPortalLayout = isPerformerPage(appContext) && isDesktop;

  // `in` check is needed here because `model` is the generic page model type
  // (from usePageModelData) which doesn't include performerGridSurface.
  // The `in` guard narrows the type at runtime so TS allows the property access.
  const isGaShoppingMode = "performerGridSurface" in model && model.performerGridSurface?.type === "ga-festival";
  const titleContent = isGaShoppingMode ? categoryName : isParking ? parkingTitleContent : categoryTitle;
  const isConcert = hasCategorySummary && model.categorySummary?.topLevelCategoryId === TOP_LEVEL_CATEGORY.CONCERT;
  const tourTitle =
    "tourFilterMetadata" in model && model.tourFilterMetadata ? model.tourFilterMetadata.tourName : undefined;

  return (
    // @ts-expect-error -- npm core giving issues not providing `span`
    <Col span={isPortalLayout ? undefined : COL_SPAN_PROP}>
      <RemoveFeature
        id={FeatureSetEnum.TMPerformerVGGReplaceLogoWithCategoryName}
        canParticipate={() => !isStubHub && isPerformerPage(appContext)}
        feature={() => (
          <SmallerPerformerFontFeature>
            {/** override for Tim McGraw primary event */}
            {hasCategorySummary && model.categorySummary?.categoryId === TIM_MCGRAW_EXCLUSIVE_CATEGORY_ID
              ? categoryName
              : titleContent}
          </SmallerPerformerFontFeature>
        )}
      />
      <AddFeature
        id={FeatureSetEnum.TMPerformerTourNameSubtitle}
        recordType={RecordParticipationTypes.IN_VIEW}
        canParticipate={() => isConcert && !!tourTitle}
        feature={() => <span className={desktopTourSubtitleStyle}>{tourTitle}</span>}
      />
      {!isMobile && !isPortalLayout && !!genres?.length && (
        <PerformerGenresDiv>
          <MusicNoteIconWrapper>
            <MusicIcon size={IconsSize.s} />
          </MusicNoteIconWrapper>
          {genres.join(", ")}
        </PerformerGenresDiv>
      )}
    </Col>
  );
});
ArtistInfoColumn.displayName = "ArtistInfoColumn";
